/**
 * @fileoverview Admission routes component with authentication guards.
 */

import React from "react";
import {Switch} from "react-router-dom";
import {SentryRoute as Route} from "../utils/sentryRoute";

import {PageWelcome} from "../pages/page-admission-welcome";
import {PageEnterAdmission} from "../pages/page-admission-enter";
import {PageAdmissionVerifyAnswers} from "../pages/page-admission-verify-answers";
import {PageAdmissionPrelude} from "../pages/page-admission-prelude";
import {PageAdmissionExperience} from "../pages/page-admission-experience";
import {PageExpiredSession} from "../pages/page-admission-expired-session";

import {ADMISSION_WELCOME_ROUTE} from "../pages/page-admission-welcome/route";
import {ADMISSION_ENTER_ROUTE} from "../pages/page-admission-enter/route";
import {ADMISSION_VERIFY_ANSWERS_ROUTE} from "../pages/page-admission-verify-answers/route";
import {ADMISSION_PRELUDE_ROUTE} from "../pages/page-admission-prelude/route";
import {ADMISSION_EXPERIENCE_ROUTE} from "../pages/page-admission-experience/route";
import {ADMISSION_EXPIRED_SESSION_ROUTE} from "../pages/page-admission-expired-session/route";

import {UserIsLoggedOut} from "../models/admission-session/UserIsLoggedOut";
import {UserHasAdmissionEntryToken} from "../models/admission-session/UserHasAdmissionEntryToken";
import {UserHasAdmissionToken} from "../models/admission-session/UserHasAdmissionToken";
import {Page404} from "../pages/page-404";
import {TokenIsNotExpired} from "../models/admission-session/TokenIsNotExpired";
import {
  AdmissionApiClientConfiguration,
  AdmissionRestApiClientConfiguration,
} from "./useApiClientConfiguration";
import {AutoLogoutProvider} from "../contexts/AutoLogoutContext";
import {AutoLogoutWarningBar} from "../atoms/auto-logout";
import {dispatch} from "../store";
import {ErrorBoundary} from "../atoms/error-boundary";

const GuardedPageEnterAdmission = PageEnterAdmission;
const GuardedPageAdmissionVerifyAnswers = UserHasAdmissionEntryToken(
  TokenIsNotExpired(PageAdmissionVerifyAnswers),
);
const GuardedPageAdmissionPrelude = UserHasAdmissionToken(
  TokenIsNotExpired(PageAdmissionPrelude),
);
const GuardedPageAdmissionExperience = UserHasAdmissionToken(
  TokenIsNotExpired(PageAdmissionExperience),
);

/**
 * Component for authenticated admission routes with auto-logout functionality.
 *
 * @returns {JSX.Element} The authenticated admission routes with error boundary and auto-logout.
 */
const AuthenticatedAdmissionRoutes = () => {
  const handleLogout = async () => {
    await dispatch.admissionSession.logout();
  };

  return (
    <ErrorBoundary>
      <AutoLogoutProvider userType="re" onLogout={handleLogout}>
        <AutoLogoutWarningBar />
        <AdmissionApiClientConfiguration />
        <AdmissionRestApiClientConfiguration />
        <Switch>
          <Route
            path={ADMISSION_ENTER_ROUTE}
            component={GuardedPageEnterAdmission}
          />
          <Route
            path={ADMISSION_VERIFY_ANSWERS_ROUTE}
            component={GuardedPageAdmissionVerifyAnswers}
          />
          <Route
            path={ADMISSION_PRELUDE_ROUTE}
            component={GuardedPageAdmissionPrelude}
          />
          <Route
            path={ADMISSION_EXPERIENCE_ROUTE}
            component={GuardedPageAdmissionExperience}
          />
          <Route component={Page404} />
        </Switch>
      </AutoLogoutProvider>
    </ErrorBoundary>
  );
};

/**
 * Main admission routes component that handles both public and authenticated routes.
 * Routes between welcome page, expired session, and authenticated admission flows.
 *
 * @returns {JSX.Element} The admission routes component.
 */
export const AdmissionRoutes = () => (
  <Switch>
    <Route
      path={ADMISSION_WELCOME_ROUTE}
      component={UserIsLoggedOut(PageWelcome)}
    />
    <Route
      path={ADMISSION_EXPIRED_SESSION_ROUTE}
      component={PageExpiredSession}
    />
    <Route path="/workflow" component={AuthenticatedAdmissionRoutes} />
  </Switch>
);
